import { defaults, dropRight, get, reduce } from 'lodash';

type ExtendableConfigDefaultKey = 'DEFAULT';

type ExtendableConfigKey = string | number | ExtendableConfigDefaultKey;

type ExtendableConfigDefinition<Config extends object> = Partial<
  Record<ExtendableConfigKey, ExtendableConfig<Config>>
>;

type ExtendableConfig<Config extends object> = {
  base: Partial<Config>;
  variants?: ExtendableConfigDefinition<Config>;
};

export type ExtendableConfigGetter<Config extends object> = (
  path: (string | number)[]
) => Config;

/**
 * A function used to define a configuration that can be extended with variants.
 * @param definition The base configuration definition.
 * @returns A function that can be used to get the configuration for a specific path.
 * @example
 * export const getEnquiryQuoteWorkflowMetaDef =
 *  createDefinition<WorkflowStatusMeta>({
 *   base: {
 *     label: $localize`Pending`,
 *     icon: 'more',
 *     level: 'info',
 *   },
 *   variants: {
 *     [QUOTE_WORKFLOW_STATUSES.QuoteOnHold]: {
 *       base: {
 *         label: $localize`Quote on hold`,
 *         icon: 'quote_onhold_thin',
 *         level: 'info',
 *       },
 *     },
 *  });
 *
 */
export function createDefinition<Config extends object>(
  definition: ExtendableConfig<Config>
): ExtendableConfigGetter<Config> {
  return (path: (string | number)[]) => {
    const { value } = reduce(
      path,
      (acc) => {
        const innerPath = createVariantsPath(acc.path);
        const baseValue = get(definition, [...innerPath, 'base']);
        return {
          path: dropRight(acc.path),
          value: defaults(acc.value, baseValue),
        };
      },
      { path: path, value: undefined } as {
        path: (string | number)[];
        value: unknown;
      }
    );

    return defaults(value, definition.base) as Config;
  };
}

function createVariantsPath(path: (string | number)[]) {
  return [
    'variants',
    ...reduce(
      path,
      (variantsPath, currentKey) => {
        const isLast = currentKey === path[path.length - 1];
        const curPath = [...variantsPath, currentKey];
        if (isLast) return curPath;

        return [...[...curPath, 'variants']];
      },
      [] as (string | number)[]
    ),
  ];
}
